import { Button, Spinner, TextInput, type DropdownDirection } from '@consigli/facade';
import { type ProjectParticipant } from '@consigli/types';
import {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiPlus, FiX } from 'react-icons/fi';

import { Avatar } from '../utils/participant-utils';

export interface ReassignSelection {
  meetingUserId?: string | null;
  customPartyId?: string | null;
  name?: string | null;
}

export interface ReassignResultItem {
  meetingUserId?: string;
  customPartyName?: string;
}

interface ReassignPersonPopoverProps {
  title: string;
  /** Render the title in all-caps (default). Set false for normal title casing. */
  uppercaseTitle?: boolean;
  participants: ProjectParticipant[];
  mode: 'single' | 'multi';
  allowCustom: boolean;
  allowClear?: boolean;
  align?: 'left' | 'right';
  selected: ReassignSelection[];
  customParties?: ReassignSelection[];
  isSaving: boolean;
  direction: DropdownDirection;
  anchorRef?: RefObject<HTMLElement | null>;
  onSave: (result: ReassignResultItem[]) => void;
  onClose: () => void;
  renderExtra?: ReactNode;
}

const POPOVER_WIDTH_FALLBACK = 320;
const POPOVER_GAP = 8;

const focusOnMount = (el: HTMLInputElement | null) => el?.focus();

const keyOf = (sel: ReassignSelection): string => {
  if (sel.meetingUserId) return `u:${sel.meetingUserId}`;
  if (sel.customPartyId) return `c:${sel.customPartyId}`;
  return `n:${(sel.name ?? '').trim().toLowerCase()}`;
};

export const ReassignPersonPopover: FC<ReassignPersonPopoverProps> = ({
  title,
  uppercaseTitle = true,
  participants,
  mode,
  allowCustom,
  allowClear = true,
  selected,
  customParties = [],
  isSaving,
  direction,
  align = 'left',
  anchorRef,
  onSave,
  onClose,
  renderExtra,
}) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [working, setWorking] = useState<ReassignSelection[]>(selected);

  const usePortal = anchorRef != null;
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!usePortal) return;
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = popoverRef.current?.getBoundingClientRect().width || POPOVER_WIDTH_FALLBACK;
    const left = align === 'right' ? rect.right - width : rect.left;
    const top = direction === 'up' ? rect.top - POPOVER_GAP : rect.bottom + POPOVER_GAP;
    setPos({ top, left });
  }, [usePortal, anchorRef, align, direction]);

  useEffect(() => {
    if (!usePortal) return;
    const closeOnScroll = (event: Event) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [usePortal, onClose]);

  useEffect(() => {
    if (!usePortal) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [usePortal, anchorRef, onClose]);

  const query = search.trim();
  const q = query.toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return participants;
    return participants.filter(
      (p) => (p.name ?? '').toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q),
    );
  }, [participants, q]);

  const filteredCustomParties = useMemo(() => {
    if (!allowCustom || customParties.length === 0) return [];
    if (!q) return customParties;
    return customParties.filter((c) => (c.name ?? '').toLowerCase().includes(q));
  }, [allowCustom, customParties, q]);

  const canAddCustom = useMemo(() => {
    if (!allowCustom || !query) return false;
    const matchesParticipant = participants.some(
      (p) =>
        (p.name ?? '').trim().toLowerCase() === q || (p.email ?? '').trim().toLowerCase() === q,
    );
    const matchesCustom = customParties.some((c) => (c.name ?? '').trim().toLowerCase() === q);
    return !matchesParticipant && !matchesCustom;
  }, [allowCustom, query, q, participants, customParties]);

  const workingKeys = useMemo(() => new Set(working.map(keyOf)), [working]);
  const initialKeys = useMemo(() => new Set(selected.map(keyOf)), [selected]);

  const isSelectedKey = useCallback((key: string) => workingKeys.has(key), [workingKeys]);

  const toggleSelection = useCallback(
    (item: ReassignSelection) => {
      setWorking((prev) => {
        if (mode === 'single') {
          if (allowClear && prev.length === 1 && keyOf(prev[0]) === keyOf(item)) return [];
          return [item];
        }
        return prev.some((w) => keyOf(w) === keyOf(item))
          ? prev.filter((w) => keyOf(w) !== keyOf(item))
          : [...prev, item];
      });
    },
    [mode, allowClear],
  );

  const toggleParticipant = useCallback(
    (p: ProjectParticipant) => toggleSelection({ meetingUserId: p.id, name: p.name || p.email }),
    [toggleSelection],
  );

  const selectTypedName = useCallback(() => {
    const name = query;
    if (!name) return;
    const item: ReassignSelection = { name };
    setWorking((prev) => {
      if (mode === 'single') return [item];
      return prev.some((w) => keyOf(w) === keyOf(item)) ? prev : [...prev, item];
    });
    setSearch('');
  }, [query, mode]);

  const removeChip = useCallback((sel: ReassignSelection) => {
    setWorking((prev) => prev.filter((w) => keyOf(w) !== keyOf(sel)));
  }, []);

  const chips = useMemo(() => {
    if (mode === 'multi') return working;
    return allowClear ? working : working.filter((w) => !w.meetingUserId);
  }, [mode, allowClear, working]);

  const hasChange = useMemo(() => {
    if (workingKeys.size !== initialKeys.size) return true;
    for (const k of workingKeys) if (!initialKeys.has(k)) return true;
    return false;
  }, [workingKeys, initialKeys]);

  const emptyBlocked = !allowClear && working.length === 0;

  const handleSave = useCallback(() => {
    if (isSaving || !hasChange || (!allowClear && working.length === 0)) return;
    const result: ReassignResultItem[] = working
      .map((w) =>
        w.meetingUserId
          ? { meetingUserId: w.meetingUserId }
          : { customPartyName: (w.name ?? '').trim() },
      )
      .filter((r) => r.meetingUserId || r.customPartyName);
    onSave(result);
  }, [isSaving, hasChange, allowClear, working, onSave]);

  const listIsEmpty = filtered.length === 0 && filteredCustomParties.length === 0 && !canAddCustom;

  const positionClasses = usePortal
    ? 'fixed'
    : `absolute ${align === 'right' ? 'right-0' : 'left-0'} ${
        direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
      }`;

  const positionStyle = usePortal
    ? pos
      ? {
          top: pos.top,
          left: pos.left,
          ...(direction === 'up' ? { transform: 'translateY(-100%)' } : {}),
        }
      : { visibility: 'hidden' as const }
    : undefined;

  const content = (
    <div
      ref={popoverRef}
      className={`${positionClasses} z-30 w-80 rounded-lg border border-gray-200 bg-white shadow-lg`}
      style={positionStyle}
      role="dialog"
      aria-label={title}
    >
      <div className="p-3">
        <p
          className={`mb-2 text-xs font-semibold tracking-wide text-gray-500${
            uppercaseTitle ? ' uppercase' : ''
          }`}
        >
          {title}
        </p>
        <TextInput
          ref={focusOnMount}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canAddCustom) {
              e.preventDefault();
              selectTypedName();
            }
          }}
          placeholder={
            allowCustom ? '' : t('meeting-secretary.meetings.change-speaker-search-placeholder')
          }
          className="w-full text-sm"
          disabled={isSaving}
        />
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-gray-100 px-3 py-2">
          {chips.map((sel) => (
            <span
              key={keyOf(sel)}
              className="inline-flex items-center gap-1 rounded-full bg-brand-light/50 py-0.5 pl-1 pr-1.5 text-xs text-gray-800"
            >
              <Avatar name={sel.name} className="!h-5 !w-5 !text-[10px] !ring-0" />
              <span className="max-w-[9rem] truncate">{sel.name}</span>
              <Button
                ghost
                iconOnly
                icon={FiX}
                iconSize={12}
                disabled={isSaving}
                onClick={() => removeChip(sel)}
                className="text-gray-400 hover:text-gray-700"
                aria-label={t('meeting-secretary.meetings.remove')}
              />
            </span>
          ))}
        </div>
      )}

      <div className="max-h-52 overflow-y-auto border-t border-gray-100 px-1 py-1">
        {listIsEmpty ? (
          <p className="px-3 py-4 text-center text-sm text-gray-400">
            {t('meeting-secretary.meetings.no-participants-found')}
          </p>
        ) : (
          <>
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={isSaving}
                onClick={() => toggleParticipant(p)}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <Avatar name={p.name} email={p.email} />
                <span
                  className="min-w-0 flex-1 truncate text-sm text-gray-900"
                  title={p.name ?? p.email}
                >
                  {p.name || p.email}
                </span>
                {isSelectedKey(`u:${p.id}`) && (
                  <FiCheck size={16} className="flex-shrink-0 text-brand" />
                )}
              </button>
            ))}

            {filteredCustomParties.map((c) => (
              <button
                key={keyOf(c)}
                type="button"
                disabled={isSaving}
                onClick={() => toggleSelection(c)}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <Avatar name={c.name} />
                <span
                  className="min-w-0 flex-1 truncate text-sm text-gray-900"
                  title={c.name ?? ''}
                >
                  {c.name}
                </span>
                {isSelectedKey(keyOf(c)) && (
                  <FiCheck size={16} className="flex-shrink-0 text-brand" />
                )}
              </button>
            ))}

            {canAddCustom && (
              <button
                type="button"
                disabled={isSaving}
                onClick={selectTypedName}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                  <FiPlus size={16} />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                  {t('meeting-secretary.meetings.add-custom-name-value', { name: query })}
                </span>
              </button>
            )}
          </>
        )}
      </div>

      {renderExtra}

      <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-3">
        <Button tertiary rounded onClick={onClose} disabled={isSaving}>
          {t('meeting-secretary.meetings.cancel')}
        </Button>
        <Button
          primary
          rounded
          onClick={handleSave}
          disabled={!hasChange || isSaving || emptyBlocked}
        >
          {isSaving ? <Spinner size="xsmall" /> : t('meeting-secretary.meetings.save')}
        </Button>
      </div>
    </div>
  );

  return usePortal ? createPortal(content, document.body) : content;
};
